import {
  BarChart3,
  Target,
  CheckCircle,
  Clock,
  AlertTriangle,
  Flame,
  Award,
  Eye,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

interface CountByCategory {
  kode: string;
  nama: string;
  count: number;
}

interface ExpiryAlert {
  document_id: string;
  judul: string;
  jenis_pnbp_nama: string;
  tanggal_berakhir: string;
  days_remaining: number;
  tingkat_kerahasiaan: string;
}

interface KnowledgeItem {
  id: string;
  judul: string;
  total_views: number;
  kategori: string;
}

interface KinerjaPanelProps {
  totalDocuments: number;
  approvedCount: number;
  pendingCount: number;
  approvedPct: number;
  byStatus: CountByCategory[];
  expiringSoon: number;
  expiryAlerts: ExpiryAlert[];
  knowledgeItems: KnowledgeItem[];
  tahun: number;
  onStatusClick?: (statusKode: string, statusNama: string) => void;
  onExpiryClick?: () => void;
  onViewAllDocs?: () => void;
}

const STATUS_BADGE_COLORS: Record<string, string> = {
  DRAFT: "bg-gray-100 text-gray-700",
  DIAJUKAN: "bg-yellow-100 text-yellow-800",
  DITINJAU: "bg-blue-100 text-blue-800",
  DISETUJUI: "bg-emerald-100 text-emerald-800",
  DITERBITKAN: "bg-green-100 text-green-800",
  DITOLAK: "bg-red-100 text-red-800",
  DIARSIPKAN: "bg-purple-100 text-purple-800",
};

// Simulated heatmap data per satker (in real app this would come from backend)
const HEATMAP_DATA = [
  { nama: "Satker A", scores: ["green", "green", "yellow", "red"] },
  { nama: "Satker B", scores: ["yellow", "red", "green", "green"] },
  { nama: "Kanwil X", scores: ["green", "green", "green", "green"] },
  { nama: "Kanwil Y", scores: ["green", "yellow", "yellow", "green"] },
];

const HEATMAP_COLORS: Record<string, string> = {
  green: "bg-green-500",
  yellow: "bg-yellow-400",
  red: "bg-red-500",
};

export default function KinerjaPanel({
  totalDocuments,
  approvedCount,
  pendingCount,
  approvedPct,
  byStatus,
  expiringSoon,
  expiryAlerts,
  knowledgeItems,
  tahun,
  onStatusClick,
  onExpiryClick,
  onViewAllDocs,
}: KinerjaPanelProps) {
  const navigate = useNavigate();

  const populer = knowledgeItems.filter((k) => k.kategori === "populer");
  const bestPractice = knowledgeItems.filter((k) => k.kategori === "best_practice");

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 bg-purple-50 border-b border-purple-100">
        <BarChart3 className="h-4 w-4 text-purple-600" />
        <h3 className="text-sm font-bold text-purple-800">DASHBOARD KINERJA</h3>
        <span className="text-xs text-purple-500 ml-1">(D05 - Realisasi & Data)</span>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
        {/* ── Target vs Realisasi ── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-3.5 w-3.5 text-purple-600" />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Target vs Realisasi {tahun}
            </p>
            {onViewAllDocs && (
              <button
                onClick={onViewAllDocs}
                className="ml-auto text-[10px] text-purple-600 hover:text-purple-800 underline font-medium"
              >
                Lihat Semua
              </button>
            )}
          </div>
          <div className="rounded-lg bg-gradient-to-br from-purple-50 to-indigo-50 p-3">
            <div className="flex items-baseline justify-between mb-1">
              <span className="text-xs text-gray-500">Target {tahun}</span>
              <span className="text-sm font-bold text-gray-800">{totalDocuments} dok</span>
            </div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-xs text-gray-500">Realisasi (Approved)</span>
              <span className="text-lg font-bold text-purple-700">
                {approvedCount} <span className="text-xs font-normal text-purple-500">({approvedPct}%)</span>
              </span>
            </div>
            <div className="h-4 bg-white/60 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                style={{ width: `${Math.min(approvedPct, 100)}%` }}
              />
            </div>
            <div className="flex items-center justify-between mt-1.5 text-[10px] text-gray-400">
              <span>0%</span>
              <span className="font-semibold text-purple-600">{approvedPct}%</span>
              <span>100%</span>
            </div>
          </div>
        </div>

        {/* ── Status Dokumen ── */}
        <div className="p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide font-medium mb-3">
            Status Dokumen
          </p>
          <div className="grid grid-cols-2 gap-2 mb-3">
            <button
              onClick={() => onStatusClick?.("DISETUJUI", "Approved")}
              className="rounded-lg bg-green-50 p-2.5 text-left hover:bg-green-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs text-green-700">Approved</span>
              </div>
              <p className="text-lg font-bold text-green-800 mt-0.5">{approvedCount}</p>
              <p className="text-[10px] text-green-600">{approvedPct}%</p>
            </button>
            <button
              onClick={() => onStatusClick?.("DIAJUKAN", "Pending")}
              className="rounded-lg bg-yellow-50 p-2.5 text-left hover:bg-yellow-100 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5 text-yellow-600" />
                <span className="text-xs text-yellow-700">Pending</span>
              </div>
              <p className="text-lg font-bold text-yellow-800 mt-0.5">{pendingCount}</p>
              <p className="text-[10px] text-yellow-600">
                {totalDocuments > 0 ? ((pendingCount / totalDocuments) * 100).toFixed(0) : 0}%
              </p>
            </button>
          </div>
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden flex">
            <div className="h-full bg-green-500 transition-all" style={{ width: `${approvedPct}%` }} />
            <div
              className="h-full bg-yellow-400 transition-all"
              style={{ width: `${totalDocuments > 0 ? (pendingCount / totalDocuments) * 100 : 0}%` }}
            />
          </div>
          <div className="flex flex-wrap gap-1.5 mt-3">
            {byStatus.map((s) => (
              <button
                key={s.kode}
                onClick={() => onStatusClick?.(s.kode, s.nama)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium hover:opacity-80 transition-opacity cursor-pointer ${
                  STATUS_BADGE_COLORS[s.kode] || "bg-gray-100 text-gray-700"
                }`}
              >
                {s.nama}: {s.count}
              </button>
            ))}
          </div>
        </div>

        {/* ── Expiring (30 hari) ── */}
        <div className="p-4">
          <button
            onClick={() => onExpiryClick?.()}
            className="w-full flex items-center justify-between mb-3 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
              <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                Ekspiring (30 Hari)
              </p>
            </div>
            {expiringSoon > 0 && (
              <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
                {expiringSoon}
              </span>
            )}
          </button>
          {expiryAlerts.length === 0 ? (
            <p className="text-xs text-gray-400 py-1">Tidak ada dokumen akan kadaluarsa</p>
          ) : (
            <div className="space-y-1.5">
              {expiryAlerts.map((alert) => (
                <button
                  key={alert.document_id}
                  onClick={() => navigate(`/documents/${alert.document_id}`)}
                  className="w-full flex items-center gap-2 text-left rounded-lg p-2 hover:bg-gray-50 transition-colors"
                >
                  <div
                    className={`h-2 w-2 rounded-full flex-shrink-0 ${
                      alert.days_remaining <= 7 ? "bg-red-500" : alert.days_remaining <= 14 ? "bg-yellow-500" : "bg-green-500"
                    }`}
                  />
                  <p className="text-xs font-medium text-gray-800 truncate flex-1">{alert.judul}</p>
                  <span
                    className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                      alert.days_remaining <= 7 ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {alert.days_remaining}h
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Knowledge Sharing (D07) ── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Flame className="h-3.5 w-3.5 text-orange-500" />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Knowledge Sharing (D07)
            </p>
          </div>

          {/* Populer */}
          {populer.length > 0 && (
            <div className="mb-3">
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Populer</p>
              <div className="space-y-1">
                {populer.slice(0, 3).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(`/documents/${item.id}`)}
                    className="w-full flex items-center gap-2 text-left rounded px-2 py-1.5 hover:bg-orange-50 transition-colors"
                  >
                    <Eye className="h-3 w-3 text-orange-400 flex-shrink-0" />
                    <span className="text-xs text-gray-700 truncate flex-1">{item.judul}</span>
                    <span className="text-[10px] text-gray-400">{item.total_views}x</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Best Practice */}
          {bestPractice.length > 0 && (
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5">Best Practice</p>
              <div className="space-y-1">
                {bestPractice.slice(0, 3).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigate(`/documents/${item.id}`)}
                    className="w-full flex items-center gap-2 text-left rounded px-2 py-1.5 hover:bg-amber-50 transition-colors"
                  >
                    <Award className="h-3 w-3 text-amber-500 flex-shrink-0" />
                    <span className="text-xs text-gray-700 truncate flex-1">{item.judul}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {populer.length === 0 && bestPractice.length === 0 && (
            <p className="text-xs text-gray-400">Belum ada data knowledge sharing</p>
          )}
        </div>

        {/* ── Heatmap Risk per Satker ── */}
        <div className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 className="h-3.5 w-3.5 text-red-500" />
            <p className="text-xs text-gray-500 uppercase tracking-wide font-medium">
              Heatmap Risk (per Satker)
            </p>
          </div>

          <div className="space-y-1.5">
            {/* Column headers */}
            <div className="flex items-center gap-1 pl-20">
              {["S01", "S02", "S03", "S04"].map((s) => (
                <span key={s} className="w-6 text-center text-[9px] text-gray-400 font-mono">{s}</span>
              ))}
            </div>
            {HEATMAP_DATA.map((row) => (
              <div key={row.nama} className="flex items-center gap-1">
                <span className="w-20 text-xs text-gray-600 truncate">{row.nama}</span>
                {row.scores.map((score, i) => (
                  <div
                    key={i}
                    className={`w-6 h-5 rounded ${HEATMAP_COLORS[score]} opacity-80`}
                  />
                ))}
              </div>
            ))}
          </div>

          {/* Legend */}
          <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-green-500" /> Baik
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-yellow-400" /> Warning
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded bg-red-500" /> Kritis
            </span>
          </div>
        </div>

      </div>
    </div>
  );
}
